import { useCallback, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';
import { Button, Form, Input, Modal, Table } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { ColumnsType } from 'antd/lib/table';

import { AscIco, BreadCrumbs, DescIco, SearchIco } from 'components';

import { DEFAULT_PAGE_SIZE } from 'hooks';

import { RootState } from '_reducers';

import { getLectureList, getLectureStudentList } from '_actions/LectureAction';
import CODE from 'constants/code';

import {
  LectureStudentType,
  LectureType,
  RequestGetLectureList,
  RequestGetLectureStudentList,
  ResponseGetLectureList,
  ResponseGetLectureStudentList,
} from 'types/lecture';

import { StyledLectureStudentsModal, StyledSortTableHeader, StyledUserLectures } from './styled';

export const UserLectures = () => {
  const { userData } = useSelector((state: RootState) => state.userReducer);
  const { uid } = userData;

  const [lectureData, setLectureData] = useState<ResponseGetLectureList>({
    totalSize: 0,
    totalPage: 0,
    resultData: [],
  });

  const [requestLectureList, setRequestLectureList] = useState<RequestGetLectureList>({
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    type: 1,
    sort: 0,
  });

  const [studentData, setStudentData] = useState<ResponseGetLectureStudentList>({
    totalSize: 0,
    totalPage: 0,
    resultData: [],
  });

  const [studentsModal, setStudentsModal] = useState<{
    show: boolean;
    lectureId: number;
    reqData: RequestGetLectureStudentList;
  }>({
    show: false,
    lectureId: 0,
    reqData: { page: 0, size: 0 },
  });

  const [sortState, setSortState] = useState<{
    type: RequestGetLectureList['type'];
    order: RequestGetLectureList['sort'];
  } | null>(null);

  const [form] = useForm();

  const renderSortableHeader = (label: string, columnType: RequestGetLectureList['type']) => {
    const isActive = sortState?.type === columnType;
    const isDesc = isActive && sortState?.order === 1;

    return (
      <StyledSortTableHeader
        onClick={() => handleSort(columnType)}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <p>{label}</p>
        {isActive ? isDesc ? <DescIco /> : <AscIco /> : <AscIco />}
      </StyledSortTableHeader>
    );
  };

  const columns: ColumnsType<LectureType> = [
    {
      title: 'No.',
      dataIndex: 'index',
      key: 'index',
      align: 'center',
      render: (_, record, index) => {
        return (
          lectureData.totalSize -
          index -
          ((requestLectureList.page + 1) * requestLectureList.size - requestLectureList.size)
        );
      },
    },
    {
      title: renderSortableHeader('교수명', 1),
      dataIndex: 'professorName',
      key: 'professorName',
      align: 'center',
    },
    {
      title: renderSortableHeader('강의명', 2),
      dataIndex: 'lectureName',
      key: 'lectureName',
      align: 'center',
    },
    {
      title: '수강생',
      dataIndex: 'studentNum',
      key: 'studentNum',
      align: 'center',
      render: (_, record) => {
        return (
          <p
            style={{ color: '#1890FF', cursor: 'pointer', marginBottom: 0 }}
            onClick={() => {
              setStudentsModal({
                show: true,
                lectureId: record.lectureId,
                reqData: { page: 0, size: DEFAULT_PAGE_SIZE },
              });
            }}
          >
            {record.studentNum}
          </p>
        );
      },
    },
    {
      title: renderSortableHeader('강의 시작일', 3),
      dataIndex: 'startDate',
      key: 'startDate',
      align: 'center',
    },
    {
      title: renderSortableHeader('강의 종료일', 4),
      dataIndex: 'endDate',
      key: 'endDate',
      align: 'center',
    },
  ];

  const handleSort = (columnType: RequestGetLectureList['type']) => {
    setSortState((prev) => {
      const isSameColumn = prev?.type === columnType;
      const newOrder = isSameColumn && prev.order === 1 ? 0 : 1; // ↓이면 ↑로, 아니면 ↓로

      const newSortState = {
        type: columnType,
        order: newOrder,
      };

      setRequestLectureList((prev) => ({
        ...prev,
        page: 0,
        type: newSortState.type,
        sort: newSortState.order,
      }));

      return newSortState;
    });
  };

  const handleGetLectureList = () => {
    getLectureList(uid, requestLectureList).then((res) => {
      if (Number(res.data.result) === Number(CODE.RCV_SUCCESS)) {
        setLectureData(res.data);
      }
    });
  };

  useEffect(() => {
    handleGetLectureList();
  }, [requestLectureList]);

  const onSearch = (values: any) => {
    if (values.type === 'keydown') return;

    setRequestLectureList({
      ...requestLectureList,
      page: 0,
      keyword: values.keyword || '',
    });
  };

  const handleSearchFinish = useCallback(
    (values: any) => {
      onSearch(values);
    },
    [columns, onSearch]
  );

  const studentColumns: ColumnsType<LectureStudentType> = [
    {
      title: 'No.',
      dataIndex: 'index',
      key: 'index',
      align: 'center',
      render: (_, record, index) => {
        return (
          studentData.totalSize -
          index -
          ((studentsModal.reqData.page + 1) * studentsModal.reqData.size -
            studentsModal.reqData.size)
        );
      },
    },
    {
      title: '학생명',
      dataIndex: 'studentName',
      key: 'studentName',
      align: 'center',
    },
  ];

  const handleGetLectureStudentList = () => {
    getLectureStudentList(studentsModal.lectureId, studentsModal.reqData).then((res) => {
      if (Number(res.data.result) === Number(CODE.RCV_SUCCESS)) {
        setStudentData(res.data);
      }
    });
  };

  useEffect(() => {
    if (studentsModal.show) {
      handleGetLectureStudentList();
    }
  }, [studentsModal]);

  const handleCloseModal = () => {
    setStudentsModal({
      show: false,
      lectureId: 0,
      reqData: { page: 0, size: 0 },
    });
  };

  return (
    <StyledUserLectures>
      <BreadCrumbs items={['Home', '서비스 관리', '강의 관리']} />
      <p className="page-title">강의 관리</p>
      <div className="search-wrapper">
        <Form form={form} onFinish={handleSearchFinish}>
          <Form.Item name="keyword">
            <Input
              size="large"
              prefix={<SearchIco />}
              style={{ width: 280 }}
              placeholder="교수명 혹은 강의명을 입력하세요"
              onPressEnter={handleSearchFinish}
            />
          </Form.Item>
          <Button className="search-btn" htmlType="submit">
            검색
          </Button>
        </Form>
      </div>
      <Table
        rowKey="lectureId"
        columns={columns}
        dataSource={
          lectureData
            ? lectureData.resultData.map((item, idx) => ({
                ...item,
                key: idx,
              }))
            : []
        }
        pagination={{
          total: lectureData ? lectureData.totalSize : 0,
          current: requestLectureList.page + 1,
          pageSize: requestLectureList.size,
          showSizeChanger: true,
          onChange: (page, size) => {
            setRequestLectureList({ ...requestLectureList, page: page - 1, size });
          },
          onShowSizeChange: (_, size) => {
            setRequestLectureList({ ...requestLectureList, page: 0, size });
          },
        }}
      />
      <StyledLectureStudentsModal
        title="등록한 수강생 목록"
        open={studentsModal.show}
        onCancel={handleCloseModal}
        centered
        width={500}
        footer={false}
        maskClosable={false}
      >
        <Table
          columns={studentColumns}
          dataSource={
            studentData
              ? studentData.resultData.map((item, idx) => ({
                  ...item,
                  key: idx,
                }))
              : []
          }
          pagination={{
            total: studentData ? studentData.totalSize : 0,
            current: studentsModal.reqData.page + 1,
            pageSize: studentsModal.reqData.size,
            showSizeChanger: true,
            onChange: (page, size) => {
              setStudentsModal({ ...studentsModal, reqData: { page: page - 1, size } });
            },
            onShowSizeChange: (_, size) => {
              setStudentsModal({ ...studentsModal, reqData: { page: 0, size } });
            },
          }}
          locale={{
            emptyText: <p style={{ color: '#000', marginBottom: 0 }}>등록한 수강생이 없습니다.</p>,
          }}
        />
      </StyledLectureStudentsModal>
    </StyledUserLectures>
  );
};
